import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spin, Input } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  LoadingOutlined,
  UserOutlined,
  CreditCardOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useBillsHistory } from "../../../../../hooks/useReports";
import { useUsers } from "../../../../../hooks/useUsers";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { useRestoreBill } from "../../../../../hooks/useBills";
import { useBillFiscalInvoice } from "../../../../../hooks/useBillFiscalInvoice";
import { useInvoiceCancellationProtocol } from "../../../../../hooks/useInvoiceCancellationProtocol";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import {
  isRestoreDuplicateNameConflict,
  isUnknownFiscalOutcome,
} from "../../../../../api/bills";
import { creditNoteOutcomeOf } from "../../../../../lib/fiscalInvoice";
import { ConfirmActionModal } from "../ConfirmActionModal";
import type { RecordedBillTip } from "../payment/useBillCloseTip";
import {
  recordedBillDepositOf,
  type RecordedBillDeposit,
} from "../payment/useCloseDeposit";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
  SoftPrimaryButton,
} from "../../modalPrimitives";

interface RestoreBillModalProps {
  open: boolean;
  onClose: () => void;
  /** Право `change_payment_method` — само тогава се показва третият бутон. */
  canChangePaymentMethod?: boolean;
  /**
   * Извиква се с id-то на възстановената сметка (СЪЩОТО id — възстановяването
   * я отваря наново на място), когато операторът е натиснал „Смяна на начин
   * на плащане" — родителят отваря модала „Закрий" за нея, за да се избере
   * новият начин на плащане веднага.
   */
  onRestoredForPaymentChange?: (billId: number) => void;
  /**
   * Право `close_bill` — подаден ли е, нефискализираните редове (total == 0 и
   * без `receipt_number` — сума 0 има и бонът на сметка, платена изцяло с
   * депозит; същото правило, по което BE допуска `/close/retry`) показват
   * бутонче „Фискализирай". То затваря този модал и родителят отваря
   * „Закрий" (mode="retry") за сметката — операторът пак избира метод на
   * плащане (с депозит — не: методът и депозитът са записаните).
   */
  onFiscalize?: (bill: {
    id: number;
    name: string;
    totalAmount: string;
    /** Бакшишът, записан при закриването — „Закрий" го показва само за
     *  четене (повторният опит не го сменя). */
    tip: RecordedBillTip;
    /** Платена с депозит — „Закрий" показва записания депозит, а повторът е
     *  с празно тяло (BE ползва записаните метод и депозит). */
    deposit: RecordedBillDeposit | null;
  }) => void;
}

type TimeRange = 30 | 60 | 120;

// Кой бутон е стартирал възстановяването — определя само какво става СЛЕД
// успеха (нищо, или отваряне на модала „Закрий" за новата сметка).
type RestoreIntent = "restore" | "changePayment";

export const RestoreBillModal = ({
  open,
  onClose,
  canChangePaymentMethod = false,
  onRestoredForPaymentChange,
  onFiscalize,
}: RestoreBillModalProps) => {
  const { t, i18n } = useTranslation();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [range, setRange] = useState<TimeRange>(30);
  const [selectedBillId, setSelectedBillId] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  // Висящ въпрос „ще дублира маса с това име — продължавам ли?" (BE върна 409).
  // Пази и намерението, за да продължи със същия бутон след потвърждение.
  const [duplicateNamePending, setDuplicateNamePending] =
    useState<RestoreIntent | null>(null);
  // RRN на връщането от терминала — по избор; отива в refundRrn на сторното.
  const [refundRrn, setRefundRrn] = useState("");

  useEffect(() => {
    if (open) {
      setRange(30);
      setSelectedBillId(null);
      setDuplicateNamePending(null);
      setRefundRrn("");
    }
  }, [open]);

  // Прозорец: последните 2 часа (макс. bucket в UI). Server-side филтрация
  // по час → лек payload; client-side group-ване по 30/60/120 min toggle.
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 2 * 60 * 60 * 1000);
    const fmt = (d: Date) => {
      const pad = (n: number) => n.toString().padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    return { startDate: fmt(start), endDate: fmt(now) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { data, isLoading, error } = useBillsHistory(
    {
      statuses: ["closed"],
      start_date: startDate,
      end_date: endDate,
      date_field: "closed_at",
    },
    { enabled: open },
  );
  const bills = useMemo(() => data?.items ?? [], [data]);

  const { data: users = [] } = useUsers(undefined, undefined, undefined, { enabled: open });
  const { data: paymentMethods = [] } = usePaymentMethods({ enabled: open });
  const restoreMutation = useRestoreBill();

  // Bucket-и: бил-ите от последните 2h се групират по closed_at в 30/60/120
  // мин (кумулативни — 30 е под-множество на 60, 60 на 120). Така toggle-ът
  // показва брой за всеки bucket, без втори request.
  const eligibleBills = useMemo(() => {
    return bills
      .filter((b) => {
        if (!b.closed_at) return false;
        if (b.statistics.active_items === 0) return false;
        return true;
      })
      .sort((a, b) => {
        const ta = a.closed_at ? new Date(a.closed_at).getTime() : 0;
        const tb = b.closed_at ? new Date(b.closed_at).getTime() : 0;
        return tb - ta;
      });
  }, [bills]);

  // Кумулативни bucket-и — toggle-ът е „колко назад гледам", не интервал:
  // закритата преди 15 мин сметка се вижда и в „30 мин", и в „60 мин",
  // и в „2 часа".
  const sortedBills = useMemo(() => {
    const lower = Date.now() - range * 60 * 1000;
    return eligibleBills.filter((b) => {
      if (!b.closed_at) return false;
      return new Date(b.closed_at).getTime() >= lower;
    });
  }, [eligibleBills, range]);

  const selectedBill = useMemo(
    () => sortedBills.find((b) => b.id === selectedBillId) ?? null,
    [sortedBills, selectedBillId],
  );
  const selectedBillName = selectedBill?.name ?? "";
  // Сметка с фискална фактура: възстановяването е кредитно известие от
  // апарата — казва се ПРЕДИ действието (BE решава сам и без това).
  const selectedFiscalInvoice = useBillFiscalInvoice(selectedBill);
  // Софтуерната фактура на сметката се анулира сама — после протоколът за нея.
  const cancellationProtocol = useInvoiceCancellationProtocol(message);

  // Теглене с пари по сметката (consumed/unknown/void_failed): сторното НЕ
  // минава през терминала — предупреждаваме и искаме референция по избор.
  const selectedCardPayment = useMemo(
    () =>
      selectedBill?.card_payments?.find(
        (payment) => payment.status !== "voided",
      ) ?? null,
    [selectedBill],
  );

  const getWaiterName = (waiterId: number | null) => {
    if (!waiterId) return null;
    return users.find((u) => u.id === waiterId)?.names || null;
  };

  const getPaymentMethodLabel = (
    pmId: number | null,
    fallback: string | null,
  ) => {
    if (!pmId) return fallback;
    const pm = paymentMethods.find((p) => p.id === pmId);
    if (!pm) return fallback;
    return i18n.language === "en" ? pm.name_en : pm.name_bg;
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleRestore = useCallback(
    async (
      allowDuplicateName = false,
      intent: RestoreIntent = "restore",
    ) => {
    if (!selectedBillId || isRestoring) return;
    setIsRestoring(true);
    try {
      const result = await restoreMutation.mutateAsync({
        billId: selectedBillId,
        data: {
          // Причината се записва в bill_restores и се чете в „История на
          // операциите" — бутонът казва защо е възстановена.
          reason:
            intent === "changePayment"
              ? "Смяна на плащане"
              : "Възстановяване на сметка",
          isDefective: false,
          allowDuplicateName,
          refundRrn: refundRrn.trim() || undefined,
        },
      });
      onClose();

      const fiscalResult = result?.fiscal_result;
      // Сметка с фискална фактура — сторното е кредитно известие: номерата му,
      // а отпечатано, но незаписано във „Фактури" — текстът на BE, кехлибарено
      // и постоянно (не се издава наново).
      const creditNote = creditNoteOutcomeOf(result, t);
      if (!fiscalResult || fiscalResult.status === "success") {
        if (creditNote?.kind === "recordFailed") notifyPrinterTimeout(creditNote.text);
        else message.success(creditNote?.text ?? t("restoreBill.success"));
      } else if (isUnknownFiscalOutcome(fiscalResult.status)) {
        notifyPrinterTimeout(fiscalResult.error || t("closeTable.printerTimeout"));
      } else {
        notifyPrinterError(fiscalResult.error || t("closeTable.printerError"));
      }
      const fiscalRefused =
        !!fiscalResult &&
        fiscalResult.status !== "success" &&
        !isUnknownFiscalOutcome(fiscalResult.status);

      // Анулираната софтуерна фактура → „Протокол за анулиране?" (фискалната не се
      // анулира — кредитно известие). При „Смяна на плащане" веднага следва
      // „Закрий" — тогава протоколът е от „Справки → Фактури" (прегледът ѝ).
      if (
        intent === "restore" &&
        !fiscalRefused &&
        !selectedFiscalInvoice &&
        selectedBill?.invoice_id &&
        selectedBill.invoice_number
      ) {
        cancellationProtocol.offer(selectedBill.invoice_id, selectedBill.invoice_number);
      }

      // Възстановената сметка е същата (същото id), вече отворена — модалът
      // „Закрий" се отваря за нея (изборът минава през родителя, който
      // изчаква презареждането на списъка).
      if (intent === "changePayment" && result?.id) {
        onRestoredForPaymentChange?.(result.id);
      }
    } catch (error) {
      const response = (error as { response?: { status?: number } })?.response;
      // 409 с текст = вече има отворена маса със същото име. Питаме, вместо да
      // отказваме: потвърди ли операторът, повтаряме същото с allowDuplicateName.
      // 409 с `{bg, en}` е отказът на кредитното известие (сметка с фискална
      // фактура) — потвърждение не го променя; показва се долу с причината.
      if (!allowDuplicateName && isRestoreDuplicateNameConflict(error)) {
        setDuplicateNamePending(intent);
        return;
      }
      // 502 = принтерът отказа сторно бона — BE не е възстановил нищо;
      // модалът остава отворен, грешката стои до ръчно затваряне.
      if (response?.status === 502) {
        notifyPrinterError(getApiErrorMessage(error, t("restoreBill.error")));
        return;
      }
      message.error(getApiErrorMessage(error, t("restoreBill.error")));
    } finally {
      setIsRestoring(false);
    }
    },
    [
      selectedBillId,
      isRestoring,
      refundRrn,
      restoreMutation,
      onClose,
      message,
      notifyPrinterError,
      notifyPrinterTimeout,
      t,
      onRestoredForPaymentChange,
      selectedBill,
      selectedFiscalInvoice,
      cancellationProtocol,
    ],
  );

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedBillId && !isRestoring) handleRestore();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose, handleRestore, selectedBillId, isRestoring, isDesktop]);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPad = isNarrow ? "14px 16px" : "18px 24px";

  const rangeOptions: { label: string; value: TimeRange }[] = [
    { label: t("restoreBill.range30"), value: 30 },
    { label: t("restoreBill.range60"), value: 60 },
    { label: t("restoreBill.range120"), value: 120 },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          fontFamily: R.font,
          color: R.text,
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
        }}
      >
        <ModalHeader
          icon={<UndoOutlined />}
          title={t("restoreBill.title")}
          subtitle={t("restoreBill.total", { count: sortedBills.length })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: 14,
            minHeight: 0,
            flex: 1,
          }}
        >
          {/* Range selector — segmented в amber-on-beige */}
          <div
            style={{
              display: "inline-flex",
              gap: 4,
              padding: 4,
              borderRadius: 10,
              background: R.bg,
              border: `1px solid ${R.border}`,
            }}
          >
            {rangeOptions.map((opt) => {
              const active = range === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setRange(opt.value);
                    setSelectedBillId(null);
                  }}
                  style={{
                    flex: 1,
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 8,
                    background: active ? R.surface : "transparent",
                    boxShadow: active
                      ? "0 1px 2px rgba(17,17,17,0.06)"
                      : "none",
                    border: "none",
                    color: active ? R.ink : R.textSoft,
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 120ms, color 120ms",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              minHeight: 120,
              maxHeight: "50vh",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 200,
                }}
              >
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 36, color: R.brand }}
                      spin
                    />
                  }
                />
              </div>
            ) : error ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                  color: R.danger,
                  fontSize: 13,
                }}
              >
                {String(error)}
              </div>
            ) : sortedBills.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                  color: R.textMuted,
                  fontSize: 13,
                }}
              >
                {t("restoreBill.noBills")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {sortedBills.map((bill) => {
                  const waiterName = getWaiterName(bill.waiter_id);
                  const pmLabel = getPaymentMethodLabel(
                    bill.payment_method_id,
                    bill.payment_method_name,
                  );
                  const isSelected = selectedBillId === bill.id;
                  // Без бон = без номер И сума 0: платената изцяло с депозит
                  // има бон (Бон 2) със сума 0.00 — номерът го казва.
                  const canFiscalize =
                    !!onFiscalize &&
                    parseFloat(bill.total || "0") === 0 &&
                    !bill.receipt_number;
                  // Редът е div (не button), защото „Фискализирай" е истински
                  // <button> вътре в него — button в button е невалиден HTML.
                  return (
                    <div
                      key={bill.id}
                      role="button"
                      onClick={() => {
                        setSelectedBillId(bill.id);
                        setRefundRrn("");
                      }}
                      className={`cursor-pointer rounded-[10px] border px-3 py-2.5 text-left transition-colors duration-100 ${
                        isSelected
                          ? "border-pos-accent/30 bg-pos-ready-bg"
                          : "border-pos-line2 bg-pos-card"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                          <div
                            className={`truncate text-sm font-bold tracking-[-0.2px] ${
                              isSelected ? "text-pos-accent-ink" : "text-pos-ink"
                            }`}
                            title={bill.name}
                          >
                            {bill.name}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-pos-muted">
                            {bill.closed_at && (
                              <span className="inline-flex items-center gap-1">
                                <ClockCircleOutlined className="text-[11px]" />
                                <span className="font-pos-mono">
                                  {formatTime(bill.closed_at)}
                                </span>
                              </span>
                            )}
                            {waiterName && (
                              <span className="inline-flex min-w-0 items-center gap-1">
                                <UserOutlined className="text-[11px]" />
                                <span className="truncate">{waiterName}</span>
                              </span>
                            )}
                            {pmLabel && (
                              <span className="inline-flex min-w-0 items-center gap-1">
                                <CreditCardOutlined className="text-[11px]" />
                                <span className="truncate">{pmLabel}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        {canFiscalize && (
                          <button
                            type="button"
                            onClick={(event) => {
                              // Не избира реда — самостоятелно действие.
                              event.stopPropagation();
                              onClose();
                              onFiscalize({
                                id: bill.id,
                                name: bill.name,
                                totalAmount: bill.total_amount,
                                tip: {
                                  tip_amount: bill.tip_amount,
                                  tip_payment_method_id: bill.tip_payment_method_id,
                                  tip_snapshot: bill.tip_snapshot,
                                },
                                deposit: recordedBillDepositOf(bill),
                              });
                            }}
                            className="shrink-0 cursor-pointer rounded-lg border border-pos-danger-brd bg-pos-danger-bg px-2.5 py-1.5 text-xs font-semibold text-pos-danger-ink transition-colors duration-100 hover:border-pos-danger-ink"
                          >
                            {t("restoreBill.fiscalize")}
                          </button>
                        )}
                        <span
                          className={`shrink-0 font-pos-mono text-[15px] font-bold tabular-nums ${
                            isSelected ? "text-pos-accent-ink" : "text-pos-ink"
                          }`}
                        >
                          {Number(bill.total_amount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {selectedFiscalInvoice && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 10,
                background: R.brandTint,
                border: "1px solid color-mix(in srgb, var(--color-brand) 32%, transparent)",
                fontSize: 12.5,
                fontWeight: 600,
                color: R.brandDeep,
              }}
            >
              <FileTextOutlined style={{ fontSize: 14, marginTop: 1 }} />
              <span>
                {t("fiscalInvoice.storno.restoreContent", {
                  number: selectedFiscalInvoice.invoice_number,
                })}
                {canChangePaymentMethod && (
                  <> {t("fiscalInvoice.storno.changePaymentNote")}</>
                )}
              </span>
            </div>
          )}
          {selectedCardPayment && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 10,
                background: R.brandTint,
                border: "1px solid color-mix(in srgb, var(--color-brand) 32%, transparent)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: R.brandDeep,
                }}
              >
                <CreditCardOutlined style={{ fontSize: 14, marginTop: 1 }} />
                <span>
                  {selectedCardPayment.rrn
                    ? t("restoreBill.cardWarning", {
                        rrn: selectedCardPayment.rrn,
                      })
                    : t("restoreBill.cardWarningNoRrn")}
                </span>
              </div>
              <Input
                value={refundRrn}
                onChange={(e) => setRefundRrn(e.target.value)}
                placeholder={t("restoreBill.cardRefundPlaceholder")}
                maxLength={32}
                allowClear
              />
            </div>
          )}
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            disabled={isRestoring}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          {canChangePaymentMethod && (
            <SoftPrimaryButton
              label={t("restoreBill.changePaymentMethod")}
              onClick={() => handleRestore(false, "changePayment")}
              disabled={!selectedBillId}
              loading={isRestoring}
            />
          )}
          <PrimaryButton
            label={t("restoreBill.restore")}
            onClick={() => handleRestore()}
            disabled={!selectedBillId}
            loading={isRestoring}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>

      <ConfirmActionModal
        open={duplicateNamePending !== null}
        title={t("duplicateTableName.title")}
        message={t("duplicateTableName.restoreMessage", {
          name: selectedBillName,
        })}
        question={t("duplicateTableName.question", { name: selectedBillName })}
        confirmLabel={
          duplicateNamePending === "changePayment"
            ? t("restoreBill.changePaymentMethod")
            : t("restoreBill.restore")
        }
        onCancel={() => setDuplicateNamePending(null)}
        onConfirm={() => {
          const intent = duplicateNamePending ?? "restore";
          setDuplicateNamePending(null);
          void handleRestore(true, intent);
        }}
      />
    </Modal>
  );
};
